/**
 * The strings of the exam-wrapper sets (pedagogy spec §7.4, step 2.8c): "Vilken princip?" (E5), the
 * timed pair (E6) and the Today row for the exam's suggestions (E7). Kept apart from the rest of the
 * practice dictionary like `today/dict.ts`. Swedish says "moment" for the course's chapters and
 * "idag"; English says "module".
 */
import type { LocalizedDictionary } from "@client/i18n";

export const setsDict: LocalizedDictionary = {
  sv: {
    "practice.whichPrinciple.title": "Vilken princip?",
    "practice.whichPrinciple.intro":
      "Läs uppgiften och välj principen den bygger på. Du behöver inte räkna.",
    "practice.whichPrinciple.note":
      "Valet sparas som övning men ger inga poäng. Väljer du fel visar vi rätt princip.",
    "practice.whichPrinciple.unsaved": "Valet är inte sparat än. Försök igen för att gå vidare.",
    "practice.whichPrinciple.summary": "{right} av {total} principer rätt",
    "practice.whichPrinciple.missed": "Principer att öva mer på",
    "practice.whichPrinciple.short.one":
      "Bara {count} uppgift i de valda momenten har ett planeringssteg.",
    "practice.whichPrinciple.short.other":
      "Bara {count} uppgifter i de valda momenten har ett planeringssteg.",
    "practice.whichPrinciple.empty":
      "Det finns inga uppgifter med planeringssteg i de valda momenten än.",
    "practice.set.starting": "Startar övningen …",
    "practice.set.toSetup": "Till övningssidan",
    "practice.timed.intro": "Du har {minutes} min för två uppgifter. Lös på papper som på tentan.",
    "practice.timed.introCount":
      "Du har {minutes} min för {count} uppgifter. Lös på papper som på tentan.",
    "practice.drill.label": "{minutes} min · {count} uppgifter",
    "practice.drill.title": "Tidsatt övning",
    "practice.drill.hint": "Uppgifter från alla öppna moment, på tid. Inget lämnas in automatiskt.",
    "practice.drill.empty":
      "Det finns inte tillräckligt många öppna uppgifter än för den här övningen.",
    "practice.timed.remaining": "Tid kvar: {time}",
    "practice.timed.fiveLeft": "Fem minuter kvar.",
    "practice.timed.over":
      "Tiden är ute. Skriv klart raden du håller på med och rätta sedan det du hann.",
    "practice.timed.empty": "Det finns inte två tentauppgifter i de valda momenten än.",
    "practice.today.extras.title": "Från din övningstenta",
    "practice.today.extras.description":
      "Förslag du lade till från resultatet av din övningstenta. De ligger kvar i en vecka.",
    "practice.today.extras.label": "Förslag från din övningstenta",
    "practice.today.extra.whichPrinciple.title": "Vilken princip?",
    "practice.today.extra.whichPrinciple.body":
      "Välj princip för en rad uppgifter, bara planeringssteget.",
    "practice.today.extra.timedPair.title": "Två uppgifter på tid",
    "practice.today.extra.timedPair.body":
      "Två tentauppgifter på tid, i samma takt som på din övningstenta.",
    "practice.today.extra.setup.title": "Öva uppställning",
    "practice.today.extra.setup.body":
      "Öppna uppgifter där du tappade poäng på uppställningen. Skriv upp modellen och sambanden innan du räknar.",
    "practice.today.extra.accuracy.title": "Räkna noggrant",
    "practice.today.extra.accuracy.body":
      "Numeriska uppgifter. Kontrollera enheter och tiopotenser innan du svarar.",
    "practice.today.extra.generic": "Förslag från din övningstenta",
  },
  en: {
    "practice.whichPrinciple.title": "Which principle?",
    "practice.whichPrinciple.intro":
      "Read the problem and choose the principle it rests on. Do not calculate.",
    "practice.whichPrinciple.note":
      "Your pick is saved as practice but gives no points. If you pick the wrong one, we show the right principle.",
    "practice.whichPrinciple.unsaved": "Your pick is not saved yet. Try again to move on.",
    "practice.whichPrinciple.summary": "{right} of {total} principles right",
    "practice.whichPrinciple.missed": "Principles to practise more",
    "practice.whichPrinciple.short.one":
      "Only {count} problem in the chosen modules has a plan step.",
    "practice.whichPrinciple.short.other":
      "Only {count} problems in the chosen modules have a plan step.",
    "practice.whichPrinciple.empty":
      "There are no problems with a plan step in the chosen modules yet.",
    "practice.set.starting": "Starting the practice …",
    "practice.set.toSetup": "To the practice page",
    "practice.timed.intro":
      "You have {minutes} min for two problems. Solve on paper as in the exam.",
    "practice.timed.introCount":
      "You have {minutes} min for {count} problems. Solve them on paper, as in the exam.",
    "practice.drill.label": "{minutes} min · {count} problems",
    "practice.drill.title": "Timed practice",
    "practice.drill.hint":
      "Open problems from every open module against the clock. Nothing is handed in for you.",
    "practice.drill.empty": "There are not enough open problems yet for this drill.",
    "practice.timed.remaining": "Time left: {time}",
    "practice.timed.fiveLeft": "Five minutes left.",
    "practice.timed.over":
      "Time is up. Finish the line you are writing, then grade what you managed.",
    "practice.timed.empty": "There are not two exam-style problems in the chosen modules yet.",
    "practice.today.extras.title": "From your practice exam",
    "practice.today.extras.description":
      "Suggestions you added from the result of your practice exam. They stay for a week.",
    "practice.today.extras.label": "Suggestions from your practice exam",
    "practice.today.extra.whichPrinciple.title": "Which principle?",
    "practice.today.extra.whichPrinciple.body":
      "Choose the principle for a run of problems, the plan step only.",
    "practice.today.extra.timedPair.title": "Timed pair",
    "practice.today.extra.timedPair.body":
      "Two exam-style problems against the clock, at the same pace as on your practice exam.",
    "practice.today.extra.setup.title": "Practise set-up",
    "practice.today.extra.setup.body":
      "Open problems where you lost points on the set-up. Write down the model and the relations before you calculate.",
    "practice.today.extra.accuracy.title": "Careful calculation",
    "practice.today.extra.accuracy.body":
      "Numeric problems. Check units and powers of ten before you answer.",
    "practice.today.extra.generic": "Suggestion from your practice exam",
  },
};
